import { Close } from "@mui/icons-material";
import {
  Box,
  Grid,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { getActiveBillingEntity, getBillingEntity } from "api/services/billingEntity";
import Loader from "components/Loader";
import { useState } from "react";
import { useQuery } from "react-query";
import { useDispatch, useSelector } from "react-redux";
import {
  handleApprovalChange,
  handleBillingEntityChange,
  selectEstimate,
  handleUpdateTermsAndConditions,
  handleFieldChange
} from "redux/reducers/createEstimateSlice";
import { ResType } from "types";
import { StyledSelectedBox } from "pages/billing/styles";
import SectionHeading from "../SectionHeading";
import { placeOfSupplyStates } from "data/placeOfSupply";

function BillingEntityDetails() {
  const { billingEntityAddress, approvalHierarchy, billingEntity, locationOfSupply, hasGst } =
    useSelector(selectEstimate);
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);

  const [gstNumber, setGstNumber] = useState('NA');
  const [panNumber, setPanNumber] = useState(null);
  const { data, isLoading }: ResType = useQuery(
    ["billing-entities"],
    getActiveBillingEntity,
    {
      onSuccess: (res: any) => {
        setTimeout(() => {
          const defaultEntity = res?.data.find((item: any) => item.default);
          dispatch(handleFieldChange({ key: 'hasGst', value: defaultEntity.hasGst }));
          dispatch(handleFieldChange({ key: 'logoUrl', value: defaultEntity?.logStorage?.fileUrl }));
          dispatch(handleFieldChange({ key: 'signatureUrl', value: defaultEntity?.signatureStorage?.fileUrl }));
          dispatch(handleBillingEntityChange({ billingEntity: defaultEntity }));
          dispatch(handleUpdateTermsAndConditions(defaultEntity.terms));
          if (defaultEntity?.locationOfSupply) {
            const locationOfSupply = placeOfSupplyStates.find(
              (o: any) => o.split('-')[1] === defaultEntity?.locationOfSupply
            ) || "NA";
            dispatch(handleFieldChange({ key: "locationOfSupply", value: locationOfSupply }));
          }
          if (defaultEntity?.creditPeriod) {
            dispatch(handleFieldChange({ key: "terms", value: defaultEntity.creditPeriod }));
          }
          setGstNumber(defaultEntity?.gstNumber || "NA");
          setPanNumber(defaultEntity?.panNumber || null);

        }, 100)
      },
    }
  );

  const handleChange = (e: any) => {
    let billingEntity = data?.data?.find(
      (item: any) => item.id === e.target.value
    );
    dispatch(handleFieldChange({ key: 'hasGst', value: billingEntity.hasGst }));
    dispatch(handleFieldChange({ key: 'logoUrl', value: billingEntity?.logStorage?.fileUrl }))
    dispatch(handleFieldChange({ key: 'signatureUrl', value: billingEntity?.signatureStorage?.fileUrl }));
    dispatch(handleUpdateTermsAndConditions(billingEntity?.terms));
    setGstNumber(billingEntity?.gstNumber);
    setPanNumber(billingEntity?.panNumber || null);
    const locationOfSupply = placeOfSupplyStates.find((o: any) => o.split('-')[1] === billingEntity?.locationOfSupply);
    dispatch(handleFieldChange({ key: "locationOfSupply", value: locationOfSupply || "" }));

    dispatch(handleFieldChange({ key: "terms", value: billingEntity.creditPeriod || null }));

    dispatch(handleBillingEntityChange({ billingEntity }));
  };

  if (isLoading) return <Loader />;

  return (
    <>
      <Grid container spacing={2} mt={1}>
        <Grid item xs={6}>
          <Box>
            <TextField
              select
              label="Billing Entity"
              fullWidth
              size="small"
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
              name="billingEntity"
              value={billingEntity || ""}
            >
              {data?.data?.map((item: any, index: number) => (
                <MenuItem value={item?.id} key={index}>
                  {item?.tradeName}
                </MenuItem>
              ))}
            </TextField>
            <Box mt={2}>
              <SectionHeading title="Organisation Billing Information" />
              <Box p={2}>
                {/* <Box mb={1}>
                  <img src={logo} alt="logo" />
                </Box> */}
                <AddressDetail
                  title="Trade Name"
                  value={billingEntityAddress?.legalName}
                />
                <AddressDetail
                  title="Address"
                  value={getAddress(billingEntityAddress)}
                />
                {billingEntityAddress?.mobileNumber && <AddressDetail
                  title="Mobile Number"
                  value={billingEntityAddress?.mobileNumber}
                />}
                {billingEntityAddress?.email && <AddressDetail
                  title="Email ID"
                  value={billingEntityAddress?.email}
                />}

                {hasGst &&
                  <AddressDetail
                    title="PAN"
                    value={gstNumber.slice(2, -3)}
                  />
                }
                {hasGst &&
                  <AddressDetail
                    title="GSTIN"
                    value={gstNumber}
                  />
                }
                {
                  (panNumber && !hasGst) &&
                  <AddressDetail
                    title="PAN"
                    value={panNumber} />
                }

                {hasGst && <AddressDetail
                  title="Location of Supplier"
                  value={locationOfSupply}
                />}

              </Box>
            </Box>
          </Box>
        </Grid>
        <Grid item xs={6}>
          {approvalHierarchy ? (
            <StyledSelectedBox sx={{ mt: 0 }}>
              <Box display="flex" gap={1} alignItems="center">
                <Typography variant="caption">Approval Hierarchy -</Typography>
                <Typography variant="subtitle2">
                  {approvalHierarchy?.name}
                </Typography>
              </Box>
              <IconButton
                onClick={() => dispatch(handleApprovalChange(null))}
                size="small"
              >
                <Close fontSize="small" />
              </IconButton>
            </StyledSelectedBox>
          ) : (
            <></>
            // <StyledSelectBox sx={{ mt: 0 }} onClick={() => setOpen(true)}>
            //   <Typography>+ Select Approval Hierarchy</Typography>
            // </StyledSelectBox>
          )}
        </Grid>
      </Grid>
    </>
  );
}

export const AddressDetail = ({ title, value }) => {
  return (
    <Box sx={{ marginBottom: "16px" }}>
      <Grid container>
        <Grid item xs={4}>
          <Typography variant="body1">{title}</Typography>
        </Grid>
        <Grid item xs={1}>
          <span>:</span>
        </Grid>
        <Grid item xs={7}>
          <Typography variant="h6" sx={{ flex: 2 }}>
            {value}
          </Typography>
        </Grid>
      </Grid>
    </Box>
  );
};

export let getAddress = (address: any) => {
  let result = "";
  if (address?.buildingNumber) {
    result += `${address?.buildingNumber}, `;
  }
  if (address?.floorNumber) {
    result += `${address?.floorNumber}, `;
  }
  if (address?.buildingName) {
    result += `${address.buildingName}, `;
  }
  if (address?.street) {
    result += `${address.street}, `;
  }
  if (address?.location) {
    result += `${address.location}, `;
  }
  if (address?.city) {
    result += `${address.city}, `;
  }
  if (address?.district) {
    result += `${address.district}, `;
  }
  if (address?.state) {
    result += `${address.state} - `;
  }
  if (address?.pincode) {
    result += address.pincode;
  }
  return result;
};

export default BillingEntityDetails;
